import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Layout } from '../components/layout';
import {
  MapPin,
  Mail,
  Phone,
  Send,
  Building2,
  User,
  MessageSquare,
  CheckCircle2,
  Loader2,
  AlertCircle,
} from 'lucide-react';

const SITE_URL = 'https://www.theecoenviro.com';
const EMAIL_API_URL = 'https://d3lvabptck.execute-api.ap-south-1.amazonaws.com/prod/send-email';
const RECIPIENT_EMAIL = 'hello@theecoenviro.com';

const contactInfo = [
  {
    icon: MapPin,
    title: 'Head Office',
    content: '2126-A1, Vashundra Apartment, Argora By-Pass, Argora, Ranchi - 834002',
  },
  {
    icon: Building2,
    title: 'Registered Office',
    content: '2126-A1, Vashundra Apartment, Argora By-Pass, Argora, Ranchi - 834002, Jharkhand',
  },
  {
    icon: Mail,
    title: 'Email',
    content: 'hello@theecoenviro.com',
    href: 'mailto:hello@theecoenviro.com',
  },
  {
    icon: Phone,
    title: 'Phone',
    content: '+91 8619433916',
    href: 'tel:+918619433916',
  },
];

const services = [
  'Plastic Waste Management',
  'Paper & Dry Recyclables',
  'E-Waste Management',
  'EPR Services',
  'Factory Waste Management',
  'Corporate Recyclable Management',
  'RDF Processing',
  'Scrap Trading',
  'Other',
];

export function Contact() {
  const [searchParams] = useSearchParams();
  const isQuote = searchParams.get('quote') === 'true';
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    service: '',
    message: '',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      const formattedMessage = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <style>
            body { margin: 0; padding: 0; font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; background-color: #f4f7fa; }
            .container { max-width: 600px; margin: 40px auto; background: #ffffff; border-radius: 12px; overflow: hidden; box-shadow: 0 4px 20px rgba(0,0,0,0.1); }
            .header { background: linear-gradient(135deg, #166534 0%, #22c55e 100%); padding: 40px 30px; text-align: center; }
            .header h1 { color: #ffffff; margin: 0; font-size: 28px; font-weight: 700; letter-spacing: 0.5px; }
            .header p { color: #dcfce7; margin: 10px 0 0 0; font-size: 14px; }
            .content { padding: 40px 30px; }
            .field-group { margin-bottom: 24px; border-left: 4px solid #22c55e; padding-left: 16px; background: #f8fafc; padding: 16px; border-radius: 8px; }
            .field-label { color: #64748b; font-size: 12px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 6px; }
            .field-value { color: #1e293b; font-size: 16px; font-weight: 500; line-height: 1.6; word-wrap: break-word; }
            .message-box { background: #f1f5f9; border-radius: 8px; padding: 20px; margin-top: 8px; border: 1px solid #e2e8f0; }
            .message-text { color: #334155; font-size: 15px; line-height: 1.7; white-space: pre-wrap; }
            .footer { background: #f8fafc; padding: 24px 30px; text-align: center; border-top: 1px solid #e2e8f0; }
            .footer p { color: #64748b; font-size: 13px; margin: 0; }
            .badge { display: inline-block; background: #dcfce7; color: #166534; padding: 4px 12px; border-radius: 12px; font-size: 13px; font-weight: 600; margin-top: 8px; }
            .divider { height: 1px; background: linear-gradient(to right, transparent, #e2e8f0, transparent); margin: 30px 0; }
          </style>
        </head>
        <body>
          <div class="container">
            <div class="header">
              <h1>📬 New ${isQuote ? 'Quote Request' : 'Contact Form'} Submission</h1>
              <p>You have received a new inquiry from theecoenviro.com</p>
            </div>
            
            <div class="content">
              <div class="field-group">
                <div class="field-label">👤 Full Name</div>
                <div class="field-value">${formData.name}</div>
              </div>
              
              <div class="field-group">
                <div class="field-label">📧 Email Address</div>
                <div class="field-value">${formData.email}</div>
              </div>
              
              <div class="field-group">
                <div class="field-label">📱 Phone Number</div>
                <div class="field-value">${formData.phone || 'Not provided'}</div>
              </div>
              
              <div class="field-group">
                <div class="field-label">🏢 Company</div>
                <div class="field-value">${formData.company || 'Not provided'}</div>
              </div>
              
              ${
                formData.service
                  ? `
              <div class="field-group">
                <div class="field-label">🔧 Service Interested In</div>
                <div class="field-value">
                  ${formData.service}
                  <span class="badge">Service Selected</span>
                </div>
              </div>
              `
                  : ''
              }
              
              <div class="divider"></div>
              
              <div class="field-group">
                <div class="field-label">💬 Message</div>
                <div class="message-box">
                  <div class="message-text">${formData.message}</div>
                </div>
              </div>
            </div>
            
            <div class="footer">
              <p>This email was sent from theecoenviro.com contact form</p>
              <p style="margin-top: 8px; color: #94a3b8;">Received on ${new Date().toLocaleString('en-US', { dateStyle: 'full', timeStyle: 'short' })}</p>
            </div>
          </div>
        </body>
        </html>
      `;

      const response = await fetch(EMAIL_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          from: formData.email,
          to: RECIPIENT_EMAIL,
          subject: `${isQuote ? '[Quote Request]' : '[Contact Form]'} ${formData.name} - ${formData.service || 'General Inquiry'}`,
          message: formattedMessage,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to send message');
      }

      setIsSubmitted(true);
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        service: '',
        message: '',
      });
    } catch (err) {
      console.error('Error sending email:', err);
      setError(
        err instanceof Error
          ? err.message
          : 'Failed to send message. Please try again or contact us directly.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <Layout>
      <Helmet>
        {/* Primary Meta Tags */}
        <title>{isQuote ? 'Get a Quote' : 'Contact Us'} | Eco Enviro Circular Solutions</title>
        <meta
          name="title"
          content={`${isQuote ? 'Get a Quote' : 'Contact Us'} | Eco Enviro Circular Solutions`}
        />
        <meta
          name="description"
          content="Contact Eco Enviro for waste management solutions. Email: hello@theecoenviro.com, Phone: +91 8619433916. Head Office: Ranchi, Jharkhand."
        />
        <meta
          name="keywords"
          content="contact Eco Enviro, waste management quote, Ranchi waste management contact, Jharkhand recycling contact, waste solutions"
        />
        <meta name="robots" content="index, follow" />

        {/* Canonical URL */}
        <link rel="canonical" href={`${SITE_URL}/contact`} />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${SITE_URL}/contact`} />
        <meta
          property="og:title"
          content={`${isQuote ? 'Get a Quote' : 'Contact Us'} | Eco Enviro`}
        />
        <meta
          property="og:description"
          content="Contact Eco Enviro for waste management solutions. Head Office: Ranchi. Phone: +91 8619433916."
        />
        <meta property="og:site_name" content="Eco Enviro Circular Solutions" />
        <meta property="og:image" content={`${SITE_URL}/images/og-image.png`} />
        <meta property="og:locale" content="en_IN" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@theecoenviro" />
        <meta
          name="twitter:title"
          content={`${isQuote ? 'Get a Quote' : 'Contact Us'} | Eco Enviro`}
        />
        <meta
          name="twitter:description"
          content="Contact Eco Enviro for waste management solutions. Head Office: Ranchi. Phone: +91 8619433916."
        />
        <meta name="twitter:image" content={`${SITE_URL}/images/og-image.png`} />
      </Helmet>

      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-b to-white from-eco-green-50">
        <div className="container px-4 mx-auto sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mx-auto max-w-4xl text-center"
          >
            <span className="inline-block px-4 py-2 mb-6 text-sm font-medium rounded-full bg-eco-green-100 text-eco-green-700">
              {isQuote ? 'Get a Quote' : 'Contact Us'}
            </span>
            <h1 className="mb-6 text-4xl font-bold sm:text-5xl md:text-6xl text-eco-green-900">
              {isQuote ? 'Request a Quote' : "Let's Talk"}
            </h1>
            <p className="text-xl text-muted-foreground">
              {isQuote
                ? "Tell us about your waste management needs and we'll provide a customized solution."
                : "Have questions? We're here to help you build a sustainable future."}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 bg-white">
        <div className="container px-4 mx-auto sm:px-6 lg:px-8">
          <div className="grid gap-12 lg:grid-cols-3">
            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="lg:col-span-1"
            >
              <h2 className="mb-6 text-2xl font-bold text-eco-green-900">Get in Touch</h2>
              <p className="mb-8 text-muted-foreground">
                Reach out to us for any inquiries about our waste management services.
              </p>

              <div className="space-y-6">
                {contactInfo.map((item) => (
                  <div key={item.title} className="flex gap-4 items-start">
                    <div className="flex flex-shrink-0 justify-center items-center w-12 h-12 rounded-xl bg-eco-green-100">
                      <item.icon className="w-6 h-6 text-eco-green-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-eco-green-900">{item.title}</h3>
                      {item.href ? (
                        <a
                          href={item.href}
                          target={item.href.startsWith('http') ? '_blank' : undefined}
                          rel={item.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                          className="transition-colors text-muted-foreground hover:text-eco-green-600"
                        >
                          {item.content}
                        </a>
                      ) : (
                        <p className="text-muted-foreground">{item.content}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* States Served */}
              <div className="p-6 mt-10 rounded-2xl bg-eco-green-50">
                <h3 className="mb-4 font-semibold text-eco-green-900">States We Serve</h3>
                <p className="text-sm text-muted-foreground">
                  Jharkhand • Bihar • West Bengal • Madhya Pradesh • Rajasthan • Maharashtra
                </p>
              </div>

              {/* Alternative Contact */}
              <div className="p-4 mt-4 bg-white rounded-xl border border-eco-green-100">
                <p className="text-sm text-muted-foreground">
                  <span className="font-medium text-eco-green-800">Alternative:</span>{' '}
                  <a href="mailto:info@theecoenviro.com" className="hover:text-eco-green-600">
                    info@theecoenviro.com
                  </a>{' '}
                  •{' '}
                  <a href="tel:+919905347369" className="hover:text-eco-green-600">
                    +91 9905347369
                  </a>
                </p>
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="lg:col-span-2"
            >
              {isSubmitted ? (
                <div className="p-12 text-center rounded-2xl bg-eco-green-50">
                  <div className="flex justify-center items-center mx-auto mb-6 w-20 h-20 rounded-full bg-eco-green-100">
                    <CheckCircle2 className="w-10 h-10 text-eco-green-600" />
                  </div>
                  <h3 className="mb-4 text-2xl font-bold text-eco-green-900">Thank You!</h3>
                  <p className="mb-6 text-muted-foreground">
                    We've received your message and will get back to you within 24 hours.
                  </p>
                  <button
                    onClick={() => {
                      setIsSubmitted(false);
                      setError(null);
                    }}
                    className="px-6 py-3 font-medium text-white rounded-xl transition-colors bg-eco-green-600 hover:bg-eco-green-700"
                  >
                    Send Another Message
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="p-8 rounded-2xl bg-eco-green-50">
                  {error && (
                    <div className="flex gap-3 items-start p-4 mb-6 bg-red-50 rounded-xl border border-red-200">
                      <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-medium text-red-800">Failed to send message</p>
                        <p className="text-sm text-red-600">{error}</p>
                        <p className="mt-1 text-sm text-red-600">
                          You can also reach us at{' '}
                          <a href="mailto:hello@theecoenviro.com" className="underline">
                            hello@theecoenviro.com
                          </a>
                        </p>
                      </div>
                    </div>
                  )}
                  <div className="grid gap-6 sm:grid-cols-2">
                    {/* Name */}
                    <div>
                      <label
                        htmlFor="name"
                        className="block mb-2 text-sm font-medium text-eco-green-900"
                      >
                        Full Name *
                      </label>
                      <div className="relative">
                        <User className="absolute left-4 top-1/2 w-5 h-5 -translate-y-1/2 text-muted-foreground" />
                        <input
                          type="text"
                          id="name"
                          name="name"
                          required
                          value={formData.name}
                          onChange={handleChange}
                          className="py-3 pr-4 pl-12 w-full bg-white rounded-xl border border-eco-green-200 focus:outline-none focus:ring-2 focus:ring-eco-green-500 focus:border-transparent"
                          placeholder="Your name"
                        />
                      </div>
                    </div>

                    {/* Email */}
                    <div>
                      <label
                        htmlFor="email"
                        className="block mb-2 text-sm font-medium text-eco-green-900"
                      >
                        Email Address *
                      </label>
                      <div className="relative">
                        <Mail className="absolute left-4 top-1/2 w-5 h-5 -translate-y-1/2 text-muted-foreground" />
                        <input
                          type="email"
                          id="email"
                          name="email"
                          required
                          value={formData.email}
                          onChange={handleChange}
                          className="py-3 pr-4 pl-12 w-full bg-white rounded-xl border border-eco-green-200 focus:outline-none focus:ring-2 focus:ring-eco-green-500 focus:border-transparent"
                          placeholder="your@email.com"
                        />
                      </div>
                    </div>

                    {/* Phone */}
                    <div>
                      <label
                        htmlFor="phone"
                        className="block mb-2 text-sm font-medium text-eco-green-900"
                      >
                        Phone Number
                      </label>
                      <div className="relative">
                        <Phone className="absolute left-4 top-1/2 w-5 h-5 -translate-y-1/2 text-muted-foreground" />
                        <input
                          type="tel"
                          id="phone"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          className="py-3 pr-4 pl-12 w-full bg-white rounded-xl border border-eco-green-200 focus:outline-none focus:ring-2 focus:ring-eco-green-500 focus:border-transparent"
                          placeholder="+91 98765 43210"
                        />
                      </div>
                    </div>

                    {/* Company */}
                    <div>
                      <label
                        htmlFor="company"
                        className="block mb-2 text-sm font-medium text-eco-green-900"
                      >
                        Company Name
                      </label>
                      <div className="relative">
                        <Building2 className="absolute left-4 top-1/2 w-5 h-5 -translate-y-1/2 text-muted-foreground" />
                        <input
                          type="text"
                          id="company"
                          name="company"
                          value={formData.company}
                          onChange={handleChange}
                          className="py-3 pr-4 pl-12 w-full bg-white rounded-xl border border-eco-green-200 focus:outline-none focus:ring-2 focus:ring-eco-green-500 focus:border-transparent"
                          placeholder="Your company"
                        />
                      </div>
                    </div>

                    {/* Service */}
                    <div className="sm:col-span-2">
                      <label
                        htmlFor="service"
                        className="block mb-2 text-sm font-medium text-eco-green-900"
                      >
                        Service Interested In
                      </label>
                      <select
                        id="service"
                        name="service"
                        value={formData.service}
                        onChange={handleChange}
                        className="px-4 py-3 w-full bg-white rounded-xl border border-eco-green-200 focus:outline-none focus:ring-2 focus:ring-eco-green-500 focus:border-transparent"
                      >
                        <option value="">Select a service</option>
                        {services.map((service) => (
                          <option key={service} value={service}>
                            {service}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Message */}
                    <div className="sm:col-span-2">
                      <label
                        htmlFor="message"
                        className="block mb-2 text-sm font-medium text-eco-green-900"
                      >
                        Message *
                      </label>
                      <div className="relative">
                        <MessageSquare className="absolute top-4 left-4 w-5 h-5 text-muted-foreground" />
                        <textarea
                          id="message"
                          name="message"
                          required
                          rows={5}
                          value={formData.message}
                          onChange={handleChange}
                          className="py-3 pr-4 pl-12 w-full bg-white rounded-xl border resize-none border-eco-green-200 focus:outline-none focus:ring-2 focus:ring-eco-green-500 focus:border-transparent"
                          placeholder="Tell us about your waste management needs..."
                        />
                      </div>
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={isLoading}
                    className="flex gap-2 justify-center items-center px-8 py-4 mt-6 w-full font-semibold text-white bg-gradient-to-r rounded-xl shadow-lg transition-all sm:w-auto from-eco-green-600 to-eco-green-700 hover:from-eco-green-700 hover:to-eco-green-800 shadow-eco-green-500/25 disabled:opacity-70 disabled:cursor-not-allowed"
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5" />
                        {isQuote ? 'Request Quote' : 'Send Message'}
                      </>
                    )}
                  </button>
                </form>
              )}
            </motion.div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
